import { sample } from 'effector';
import { once, spread } from 'patronum';
import queryString from 'query-string';
import * as i18 from 'i18next';
import { Cookies } from 'react-cookie';
import {
  applyChatHexagonsEv,
  changeActiveCityEv,
  changeActiveMetricEv,
  changeLanguageEv,
  changeMetricDetailsEv,
  changeShowChartsEv,
  changeShowMetricEv,
  debouncedActiveYearEv,
  downloadEv,
  getChartsDataByCitiesEv,
  getCitiesEv,
  getFloodsAreaEv,
  getHexCollectionEv,
  getMudfloodsAreaEv,
  hideLoaderEv,
  resetMessagesEv,
  resetMetricDetailsEv,
  revertChatFiltersEv,
  saveChatHexagonsEv,
  setChatOpenEv,
  setMessagesEv,
  showLoaderEv,
} from './events.js';
import {
  getChartsDataByCitiesFx,
  getCitiesFx,
  getFloodsAreaFx,
  getHexCollectionFx,
  getMudfloodsAreaFx,
} from './effects.js';
import {
  $activeCity,
  $activeMetric,
  $activeYear,
  $chartsData,
  $chartsStartData,
  $chatHexagons,
  $chatMessages,
  $chatOpen,
  $cities,
  $floodsArea,
  $floodsStartArea,
  $hexagons,
  $isChatFilters,
  $language,
  $loader,
  $metricDetails,
  $mudfloodsArea,
  $mudfloodsStartArea,
  $showCharts,
  $showMetric,
  $startHexagons,
} from './stores.js';
import { $userInfo } from '../authModel/index.js';
import { perm_city_dict } from '../../dictionaries/perm_city_dict.js';
import { flyTo } from '../../utils/mapbox-utils.js';
import { city_centers } from '../../dictionaries/city_centers.js';
import { calculateThresholdsEv } from '../gradientModel/index.js';
import {
  $activeFilters,
  resetGradientEv,
} from '../activeFiltersModel/index.js';
import {
  filterFloods,
  filterHexagons,
} from '../../counters/hex_counter/hex_counter.js';
import { city_default_links } from '../../dictionaries/city_download_links.js';
import { calculateChartsData } from './utils.js';

const cookies = new Cookies();

const year_metrics = [
  'AIR-CO',
  'AIR-HCHO',
  'AIR-NO2',
  'AIR-SO2',
  'LST',
  'LST-DIF',
  'NDVI',
];

$cities.on(getCitiesFx.doneData, (_, payload) => payload.object.result.items);

$activeCity.on(changeActiveCityEv, (state, payload) => {
  flyTo(city_centers[payload], 10);
  return payload;
});

$showMetric.on(changeShowMetricEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$showCharts.on(changeShowChartsEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$loader.on(showLoaderEv, () => true).reset(hideLoaderEv);

$metricDetails
  .on(changeMetricDetailsEv, (state, payload) => payload)
  .reset(resetMetricDetailsEv);

$language.on(changeLanguageEv, (state, payload) => {
  const lng = payload.key;
  i18.changeLanguage(lng).then(() => true);
  cookies.set('language', lng);
  return lng;
});
$chatMessages
  .on(setMessagesEv, (state, payload) => payload)
  .reset(resetMessagesEv);

$isChatFilters.on(applyChatHexagonsEv, () => true).reset(revertChatFiltersEv);

$chatOpen.on(setChatOpenEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

sample({
  clock: getCitiesEv,
  target: getCitiesFx,
});

sample({
  clock: $activeCity.updates,
  target: [
    getHexCollectionEv,
    getFloodsAreaEv,
    getMudfloodsAreaEv,
    getChartsDataByCitiesEv,
  ],
});

sample({
  clock: getHexCollectionEv,
  target: getHexCollectionFx,
});

sample({
  source: [$activeMetric, $activeYear],
  clock: getHexCollectionFx.doneData,
  fn: ([activeMetric, year], clock) => {
    return clock.object.result.features.map((item) => {
      let index_main;
      if (year_metrics.includes(activeMetric)) {
        index_main =
          item.properties.metrics[activeMetric][year]?.value_max || 0;
      } else {
        index_main = item.properties[activeMetric];
      }
      return {
        ...item,
        properties: {
          ...item.properties,
          index_main,
        },
      };
    });
  },
  target: [$hexagons, $startHexagons, calculateThresholdsEv],
});

sample({
  clock: once($userInfo.updates),
  fn: (clock) => {
    if (clock.perms.length === 0) {
      if (window.location.search) {
        const { city_id } = queryString.parse(window.location.search);
        return Number(city_id);
      }
      return 1;
    }
    return perm_city_dict[clock.perms[0].slice(8)];
  },
  target: changeActiveCityEv,
});

sample({
  source: [$hexagons, $activeYear, $activeMetric, $startHexagons],
  clock: [$activeMetric.updates, $activeYear.updates],
  filter: ([hexagons, year, metric, startHexagons]) =>
    metric !== 'flood_ind' && metric !== 'mudflow_ind',
  fn: ([hexagons, year, metric, startHexagons], _) => {
    return {
      hexagons: hexagons.map((item) => {
        let index_main;
        if (year_metrics.includes(metric)) {
          if (metric === 'LST-DIF') {
            index_main = item.properties.metrics[metric][year] || 0;
          } else {
            index_main = item.properties.metrics[metric][year]?.value_max || 0;
          }
        } else {
          index_main = item.properties[metric];
        }
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main,
          },
        };
      }),
      startHexagons: startHexagons.map((item) => {
        let index_main;
        if (year_metrics.includes(metric)) {
          if (metric === 'LST-DIF') {
            index_main = item.properties.metrics[metric][year] || 0;
          } else {
            index_main = item.properties.metrics[metric][year]?.value_max || 0;
          }
        } else {
          index_main = item.properties[metric];
        }
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main,
          },
        };
      }),
    };
  },
  target: spread({
    targets: {
      hexagons: [$hexagons, calculateThresholdsEv, showLoaderEv],
      startHexagons: $startHexagons,
    },
  }),
});

sample({
  clock: changeActiveMetricEv,
  target: [$activeMetric, resetGradientEv],
});

sample({
  clock: debouncedActiveYearEv,
  target: [$activeYear, showLoaderEv],
});

sample({
  source: [$startHexagons, $activeMetric],
  clock: $activeFilters.updates,
  filter: ([_, activeMetric], __) =>
    activeMetric !== 'flood_ind' && activeMetric !== 'mudflow_ind',
  fn: ([startHexagons], activeFilters) => {
    return filterHexagons(startHexagons, activeFilters);
  },
  target: $hexagons,
});

sample({
  source: [$floodsStartArea, $activeMetric],
  clock: $activeFilters.updates,
  filter: ([_, activeMetric], __) => activeMetric === 'flood_ind',
  fn: ([floodsStartArea], activeFilters) => {
    return filterFloods(floodsStartArea, activeFilters);
  },
  target: $floodsArea,
});

sample({
  source: [$mudfloodsStartArea, $activeMetric],
  clock: $activeFilters.updates,
  filter: ([_, activeMetric], __) => activeMetric === 'mudflow_ind',
  fn: ([mudfloodsStartArea], activeFilters) => {
    return filterFloods(mudfloodsStartArea, activeFilters);
  },
  target: $mudfloodsArea,
});

sample({
  clock: getFloodsAreaEv,
  target: getFloodsAreaFx,
});

sample({
  clock: getMudfloodsAreaEv,
  target: getMudfloodsAreaFx,
});

sample({
  clock: getFloodsAreaFx.doneData,
  fn: (clock) => {
    return clock.object.result.features;
  },
  target: [$floodsArea, $floodsStartArea],
});

sample({
  clock: getMudfloodsAreaFx.doneData,
  fn: (clock) => {
    return clock.object.result.features;
  },
  target: [$mudfloodsArea, $mudfloodsStartArea],
});

sample({
  source: [$activeCity, $language],
  clock: downloadEv,
  fn: ([city, language]) => {
    window.open(city_default_links[language][city], '_blank');
  },
});

sample({
  clock: $activeCity.updates,
  fn: () => false,
  target: [resetMessagesEv, revertChatFiltersEv, setChatOpenEv],
});

sample({
  clock: saveChatHexagonsEv,
  fn: (source, clock) => {
    return {
      ...source,
      [clock.message_index]: clock.map_data,
    };
  },
  target: $chatHexagons,
});

sample({
  source: [$startHexagons, $chatHexagons],
  clock: applyChatHexagonsEv,
  fn: ([startData, chatHexagons], activeMessage) => {
    return startData.filter((item) => {
      return chatHexagons[activeMessage].includes(item.properties.zoom9_id);
    });
  },
  target: $hexagons,
});

sample({
  source: $startHexagons,
  clock: revertChatFiltersEv,
  fn: (source) => source,
  target: $hexagons,
});

sample({
  source: $userInfo,
  clock: getChartsDataByCitiesEv,
  fn: (source, clock) => {
    if (source?.perms?.length === 0) return null;
    return clock;
  },
  target: getChartsDataByCitiesFx,
});

sample({
  clock: getChartsDataByCitiesFx.doneData,
  fn: (clock) => {
    return clock.object.result;
  },
  target: [$chartsData, $chartsStartData],
});

sample({
  source: [$startHexagons, $chartsStartData],
  clock: $hexagons.updates,
  fn: ([startHexagons, chartsStartData], hexagons) => {
    if (hexagons.length !== startHexagons.length) {
      return calculateChartsData(hexagons);
    }
    return chartsStartData;
  },
  target: $chartsData,
});
